import { Component, Inject, Injector, Renderer2, HostListener, DOCUMENT } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeES from '@angular/common/locales/es';
import localePT from '@angular/common/locales/pt';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from './modules/shared/components/dialog/dialog.component';
import { CapacitorService } from './services/capacitor.service';
import { AppInfo } from './models/app-info.model';
import { RegistrationService } from './services/registration.service';
import { AuthService } from './services/auth.service';
import { ScriptService } from './services/script-service';
import { ProviderService } from './services/provider.service';
import { Provider } from './models/provider.model';
import { FlavorService } from './services/flavor.service';
import { ElementLoaderService } from './services/element-loader.service';
import * as _ from 'lodash';
import { LocalStorageService } from './services/local-storage.service';
import { ProviderCustomizeService } from './services/provider-customize.service';
import { isNativeApp } from 'src/app/util/app-platform';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent {

  whiteLabel!: boolean;
  subscription!: Subscription;
  buildNumber = environment.ENVIRONMENT != 'production' ? environment.BITBUCKET_BUILD_NUMBER : undefined;
  apiURL?: string;
  preFilteredProviderSubject = new Subject<void>();

  @HostListener('window:beforeinstallprompt', ['$event'])
  onbeforeinstallprompt(e: any) {
    e.preventDefault();
    this.flavorService.deferredPrompt = e;
  }

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  constructor(
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry,
    private matDialog: MatDialog,
    private capacitorService: CapacitorService,
    private registrationService: RegistrationService,
    private scriptService: ScriptService,
    private providerService: ProviderService,
    private flavorService: FlavorService,
    private elementLoaderService: ElementLoaderService,
    @Inject(DOCUMENT) private document: Document,
    private localStorageService: LocalStorageService,
    private providerCustomizeService: ProviderCustomizeService,
    private injector: Injector,
  ) {
    this.insertCookiesScript();
    this.checkLoadCustomCss();
    this.initializeSession();
    this.setRefreshToken();
    this.registerIcons();
    this.providerSubscribe();
    this.subscribeQueryParams();

    if(isNativeApp()) {
      this.capacitorService.initializeApp();
      this.checkAppVersion();
    }

    this.checkBrowserCompatibility();

    // Locales for date pipe
    registerLocaleData(localeES, 'es');
    registerLocaleData(localePT, 'pt');

    this.localStorageService.apiUrl$.subscribe(apiUrlLocalStorage => {
      this.apiURL = environment.ENVIRONMENT !== 'production' && !_.isEmpty(apiUrlLocalStorage) ? apiUrlLocalStorage : undefined;
    });
  }

  subscribeQueryParams(): void {
    // debounce time added to avoid the empty params event that removes the pre-filtered provider.
    this.subscription = this.route.queryParams
    .pipe(debounceTime(0), takeUntil(this.preFilteredProviderSubject))
    .subscribe(async (params) => {
      if(params.provider && !this.authService.isAuthenticated()) {
        this.setPreloadSpinnerStyle();
        this.providerService.getOneBasicRegistrationInfo(params.provider)
        .then((provider: Provider) => {
          if(provider) {
            this.preFilteredProviderSubject.next();
            this.registrationService.setProvider(provider);
            this.whiteLabel = this.registrationService.whiteLabel;
          }
        })
        .finally(() => this.removePreLoadSpinner())
      } else {
        if (!this.authService.isAuthenticated() && !this.registrationService.getPatient())
          this.registrationService.clearProvider();
      }

      if(environment.ENVIRONMENT !== 'production' && !_.isUndefined(params?.['api-url'])) {
        const apiURL = params['api-url'];
        this.localStorageService.apiUrl = apiURL;
      }
    });
  }

  checkLoadCustomCss(): void {
    const provider = this.registrationService.getProvider();
    if (provider?.customStyleSheetUrl && !this.document.getElementById("custom_stylesheet")) {
      this.elementLoaderService.appendStylesheet(this.document, provider.customStyleSheetUrl)
      .setAttribute("id", "custom_stylesheet");
    }
    this.flavorService.setFlavor(this.document, provider);
  }

  insertCookiesScript(): void {
    this.scriptService.insertCookiesScript(this.renderer);
  }

  setRefreshToken(): void {
    if (this.authService.usesIdentityPlatform()) return;

    if (this.authService.isAuthenticated()) {
      this.authService.setRefreshTokenTimeout();
    } else {
      this.authService.refreshToken();
    }
  }

  initializeSession(): void {
    this.authService.initializeSessionSync();
  }

  providerSubscribe(): void {
    this.whiteLabel = this.registrationService.whiteLabel;
    if (this.whiteLabel) this.providerCustomizeService.setThemeColor();

    this.registrationService.provider$.subscribe(
      (provider: Provider | null) => {
        this.whiteLabel = !!provider?.whiteLabel
        if (this.whiteLabel) this.providerCustomizeService.setThemeColor();
        else this.providerCustomizeService.removeThemeColor();
      }
    );
  }

  async checkAppVersion() {
    const appInfo: AppInfo = await this.capacitorService.getAppInfo();
    if(appInfo) {
      const currentBuildNumber = +environment.BITBUCKET_BUILD_NUMBER;

      if(currentBuildNumber < appInfo.minBuildNumber) {
        this.showForceUpdateDialog(false, false);
      } else if(currentBuildNumber < appInfo.currentBuildNumber) {
        this.showForceUpdateDialog(true);
      }
    }
  }

  showForceUpdateDialog(canCancel: boolean, canClose?: boolean) {
    this.matDialog.open(DialogComponent, {
      width: '80%',
      maxWidth: '350px',
      data: {
        message: "app_update_message",
        title: "app_update_title",
        closeMessage: "continue",
        canCancel: canCancel,
        canClose: canClose,
        actions: [{
          message: "app_go_to_store",
          action: () => this.capacitorService.redirectToAppStore(),
        }]
      },
      disableClose: true
    });
  }

  setPreloadSpinnerStyle() {
    const spinner = this.document.getElementById("pre-loading-spinner")
    if (spinner) spinner.style.display = "flex"
  }

  removePreLoadSpinner() {
    const spinner = this.document.getElementById("pre-loading-spinner")
    if (spinner) spinner.style.display = "none"
  }

  displayBrowserNotSupportedDialog(): void {
    this.matDialog.closeAll();

    this.matDialog.open(DialogComponent, {
      width: '80%',
      maxWidth: '350px',
      data: {
        title: "browser_not_supported_title",
        message: "browser_not_supported_message",
      },
      disableClose: true
    });
  }

  displayWhatsappBrowserNotSupportedDialog(): void {
    this.matDialog.closeAll();

    this.matDialog.open(DialogComponent, {
      width: '80%',
      maxWidth: '350px',
      data: {
        title: "browser_not_supported_title",
        message: "browser_not_supported_message_whatsapp",
      },
      disableClose: true
    });
  }

  checkBrowserCompatibility(): void {
    const userAgent = navigator.userAgent;
    let safariVersion = null;
    const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome') && !userAgent.includes('Android');
    const isWhatsAppBrowser = userAgent.toLowerCase().includes('whatsapp');

    if (isSafari) {
      const versionMatch = userAgent.match(/Version\/([\d.]+)/);
      if (versionMatch && versionMatch[1]) {
        safariVersion = parseFloat(versionMatch[1]);
      }
    }

    /*
      OpenTok 2.27 and later is not supported on Safari 14 and lower.
      https://tokbox.com/developer/sdks/js/release-notes.html
    */
    if (safariVersion && safariVersion < 15) {
      this.displayBrowserNotSupportedDialog();
      return;
    }

    /*
      WhatsApp in-app browser has issues when trying to call show as not supported.
    */
    if (isWhatsAppBrowser) {
      this.displayWhatsappBrowserNotSupportedDialog();
      return;
    }
  }

  registerIcons(): void {
    this.matIconRegistry.addSvgIcon(
      'lad',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/lad.svg')
    ).addSvgIcon(
      'app_store',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/app_store.svg')
    ).addSvgIcon(
      'apple',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/apple.svg')
    ).addSvgIcon(
      'calls',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/calls.svg')
    ).addSvgIcon(
      'doctors',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/doctors.svg')
    ).addSvgIcon(
      'facebook',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/facebook.svg')
    ).addSvgIcon(
      'google',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/google.svg')
    ).addSvgIcon(
      'heart',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/heart.svg')
    ).addSvgIcon(
      'home',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/home.svg')
    ).addSvgIcon(
      'instagram',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/instagram.svg')
    ).addSvgIcon(
      'linkedin',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/linkedin.svg')
    ).addSvgIcon(
      'outlook',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/outlook.svg')
    ).addSvgIcon(
      'play_store',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/play_store.svg')
    ).addSvgIcon(
      'questions',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/question.svg')
    ).addSvgIcon(
      'settings',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/settings.svg')
    ).addSvgIcon(
      'user',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/icon_user.svg')
    ).addSvgIcon(
      'users',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/users.svg')
    ).addSvgIcon(
      'info',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/info.svg')
    ).addSvgIcon(
      'east',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/icon_east.svg')
    ).addSvgIcon(
      'arrow',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/arrow.svg')
    ).addSvgIcon(
      'medicine',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/medicine.svg')
    ).addSvgIcon(
      'baby',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/pedia.svg')
    ).addSvgIcon(
      'gine',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/gine.svg')
    ).addSvgIcon(
      'prescription',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/prescription.svg')
    ).addSvgIcon(
      'covid',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/covid.svg')
    ).addSvgIcon(
      'chair',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/chair.svg')
    ).addSvgIcon(
      'headphones',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/headphones.svg')
    ).addSvgIcon(
      'microphone',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/microphone.svg')
    ).addSvgIcon(
      'microphone_off',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/microphone_off.svg')
    ).addSvgIcon(
      'media',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/media.svg')
    ).addSvgIcon(
      'noise',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/noise.svg')
    ).addSvgIcon(
      'phone',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/phone.svg')
    ).addSvgIcon(
      'call_phone',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/call_phone.svg')
    ).addSvgIcon(
      'phone_calling',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/phone_calling.svg')
    ).addSvgIcon(
      'sound_off',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/sound_off.svg')
    ).addSvgIcon(
      'exclamation',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/exclamation.svg')
    ).addSvgIcon(
      'videocamera',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/videocamera.svg')
    ).addSvgIcon(
      'videocamera_off',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/videocamera_off.svg')
    ).addSvgIcon(
      'upload_documents',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/upload_documents.svg')
    ).addSvgIcon(
      'apron',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/apron.svg')
    ).addSvgIcon(
      'fav-heart-filled',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/fav-heart-filled.svg')
    ).addSvgIcon(
      'fav-heart-outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/fav-heart-outline.svg')
    ).addSvgIcon(
      'calls-filled',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/calls-filled.svg')
    ).addSvgIcon(
      'pill',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/pill.svg')
    ).addSvgIcon(
      'prescription-green',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/prescription_green.svg')
    ).addSvgIcon(
      'search',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/search.svg')
    ).addSvgIcon(
      'black_question',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/black_question.svg')
    ).addSvgIcon(
      'ps',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/ps.svg')
    ).addSvgIcon(
      'people-group',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/people-group.svg')
    ).addSvgIcon(
      'calendar',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/calendar.svg')
    ).addSvgIcon(
      'isotype-lad',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/isotype-lad.svg')
    ).addSvgIcon(
      'personal_data',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/personal_data.svg')
    ).addSvgIcon(
      'cancel',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/cancel.svg')
    ).addSvgIcon(
      'arrow-down-long',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/arrow-down-long.svg')
    ).addSvgIcon(
      'calendar-2',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/calendar-2.svg')
    ).addSvgIcon(
      'arrow-down',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/arrow-down.svg')
    ).addSvgIcon(
      'trash-can',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/trash-can.svg')
    ).addSvgIcon(
      'arrow-left',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/arrow-left.svg')
    ).addSvgIcon(
      'close',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/close.svg')
    ).addSvgIcon(
      'medical-exam',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/medical-exam.svg')
    ).addSvgIcon(
      'lab-results',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/lab-results.svg')
    ).addSvgIcon(
      'electrocardiogram',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/electrocardiogram.svg')
    ).addSvgIcon(
      'resonance',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/resonance.svg')
    ).addSvgIcon(
      'ultrasound',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/ultrasound.svg')
    ).addSvgIcon(
      'xray',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/xray.svg')
    ).addSvgIcon(
      'others',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/others.svg')
    ).addSvgIcon(
      'arrow-up',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/arrow-up.svg')
    ).addSvgIcon(
      'eye-opened',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/eye-opened.svg')
    ).addSvgIcon(
      'default-img',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/default-img.svg')
    ).addSvgIcon(
      'trash-can-gray',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/trash-can-gray.svg')
    ).addSvgIcon(
      'exclamation-accent',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/exclamation-accent.svg')
    ).addSvgIcon(
      'zoom',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/zoom.svg')
    ).addSvgIcon(
      'contact',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/contact.svg')
    ).addSvgIcon(
      '4g-signal',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/4g-signal.svg')
    ).addSvgIcon(
      'call-signal',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/call-signal.svg')
    ).addSvgIcon(
      'cam-mic',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/cam-mic.svg')
    ).addSvgIcon(
      'facial-scan',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/facial-scan.svg')
    ).addSvgIcon(
      'ai_calling',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/ai_calling.svg')
    ).addSvgIcon(
      'send',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/send.svg')
    ).addSvgIcon(
      'stop',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/stop.svg')
    ).addSvgIcon(
      'refresh',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/refresh.svg')
    ).addSvgIcon(
      'lad-pt',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/lad-pt.svg')
    ).addSvgIcon(
      'lad-en',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/lad-en.svg')
    ).addSvgIcon(
      'keyboard_arrow_up',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/keyboard_arrow_up.svg')
    ).addSvgIcon(
      'upload_documents_2',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/upload_documents_2.svg')
    )
  }
}
